<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isUpdating = ref(false);
const aiEnabled = ref(false);
const aiAgentsSuperadminOnly = ref(false);
const kanbanQuickMessageEnabled = ref(false);
const kanbanBulkMessageEnabled = ref(false);
// v6.4.25: Toggle para definir local das etiquetas (header vs sidebar)
const labelsInSidebar = ref(false);
const isInitialized = ref(false);

const accountId = computed(() => getters.getCurrentAccountId.value);

const currentAccount = computed(() => {
  return getters['accounts/getAccount'].value(accountId.value) || {};
});

// Initialize from store value
watch(
  () => currentAccount.value?.settings,
  newSettings => {
    if (!isUpdating.value && newSettings) {
      aiEnabled.value = newSettings.ai_enabled ?? false;
      // v6.4.25: Default true — novas contas têm menu Agentes IA restrito ao SuperAdmin por padrão
      aiAgentsSuperadminOnly.value = newSettings.ai_agents_superadmin_only ?? true;
      // v6.4.25: Default true — novas contas têm Mensagem Rápida ativada por padrão
      kanbanQuickMessageEnabled.value = newSettings.kanban_quick_message_enabled ?? true;
      kanbanBulkMessageEnabled.value = newSettings.kanban_bulk_message_enabled ?? false;
      // v6.4.25: Default false — novas contas têm etiquetas no header por padrão
      labelsInSidebar.value = newSettings.labels_in_sidebar ?? false;
      isInitialized.value = true;
    }
  },
  { immediate: true, deep: true }
);

// Watch for user changes to aiEnabled
watch(aiEnabled, async (newValue, oldValue) => {
  // Skip if not initialized or if change came from store sync
  if (!isInitialized.value || isUpdating.value) return;
  // Skip if values are the same (initial sync)
  if (newValue === oldValue) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        ai_enabled: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_TOGGLE.SUCCESS_ENABLED')
        : t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_TOGGLE.SUCCESS_DISABLED')
    );
  } catch (error) {
    // Revert on error
    aiEnabled.value = !newValue;
    useAlert(t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_TOGGLE.ERROR'));
  } finally {
    isUpdating.value = false;
  }
});

// Watch for user changes to aiAgentsSuperadminOnly
watch(aiAgentsSuperadminOnly, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        ai_agents_superadmin_only: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_AGENTS_VISIBILITY.SUCCESS_ENABLED')
        : t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_AGENTS_VISIBILITY.SUCCESS_DISABLED')
    );
  } catch (error) {
    // Revert on error
    aiAgentsSuperadminOnly.value = !newValue;
    useAlert(t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_AGENTS_VISIBILITY.ERROR'));
  } finally {
    isUpdating.value = false;
  }
});

// Watch for user changes to kanbanQuickMessageEnabled
watch(kanbanQuickMessageEnabled, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        kanban_quick_message_enabled: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_QUICK_MESSAGE.SUCCESS_ENABLED')
        : t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_QUICK_MESSAGE.SUCCESS_DISABLED')
    );
  } catch (error) {
    kanbanQuickMessageEnabled.value = !newValue;
    useAlert(t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_QUICK_MESSAGE.ERROR'));
  } finally {
    isUpdating.value = false;
  }
});

// Watch for user changes to kanbanBulkMessageEnabled
watch(kanbanBulkMessageEnabled, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        kanban_bulk_message_enabled: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_BULK_MESSAGE.SUCCESS_ENABLED')
        : t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_BULK_MESSAGE.SUCCESS_DISABLED')
    );
  } catch (error) {
    kanbanBulkMessageEnabled.value = !newValue;
    useAlert(t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_BULK_MESSAGE.ERROR'));
  } finally {
    isUpdating.value = false;
  }
});

// v6.4.25: Watch for user changes to labelsInSidebar
watch(labelsInSidebar, async (newValue, oldValue) => {
  if (!isInitialized.value || isUpdating.value) return;
  if (newValue === oldValue) return;

  isUpdating.value = true;
  try {
    const currentSettings = currentAccount.value?.settings || {};
    await store.dispatch('accounts/update', {
      settings: {
        ...currentSettings,
        labels_in_sidebar: newValue,
      },
      options: { silent: true },
    });
    useAlert(
      newValue
        ? t('INTEGRATION_SETTINGS.OTHER_FEATURES.LABELS_LOCATION.SUCCESS_ENABLED')
        : t('INTEGRATION_SETTINGS.OTHER_FEATURES.LABELS_LOCATION.SUCCESS_DISABLED')
    );
  } catch (error) {
    labelsInSidebar.value = !newValue;
    useAlert(t('INTEGRATION_SETTINGS.OTHER_FEATURES.LABELS_LOCATION.ERROR'));
  } finally {
    isUpdating.value = false;
  }
});

onMounted(() => {
  // Ensure account data is loaded
  if (!currentAccount.value?.id) {
    store.dispatch('accounts/get');
  }
});
</script>

<template>
  <div class="mt-8">
    <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
      {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.TITLE') }}
    </h3>
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <!-- Toggle AI Box -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-violet-500 to-purple-600 text-white"
            >
              <i class="i-lucide-bot text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              aiEnabled
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="aiEnabled ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_TOGGLE.TITLE') }}
            </span>
            <ToggleSwitch v-model="aiEnabled" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_TOGGLE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- Toggle AI Agents Visibility (SuperAdmin Only) -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-teal-500 to-cyan-600 text-white"
            >
              <i class="i-lucide-eye text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              aiAgentsSuperadminOnly
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="aiAgentsSuperadminOnly ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_AGENTS_VISIBILITY.TITLE') }}
            </span>
            <ToggleSwitch v-model="aiAgentsSuperadminOnly" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.OTHER_FEATURES.AI_AGENTS_VISIBILITY.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- Toggle Kanban Quick Message -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-orange-500 to-red-500 text-white"
            >
              <i class="i-lucide-flame text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              kanbanQuickMessageEnabled
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="kanbanQuickMessageEnabled ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_QUICK_MESSAGE.TITLE') }}
            </span>
            <ToggleSwitch v-model="kanbanQuickMessageEnabled" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_QUICK_MESSAGE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- Toggle Kanban Bulk Message -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 text-white"
            >
              <i class="i-lucide-send text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              kanbanBulkMessageEnabled
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="kanbanBulkMessageEnabled ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_BULK_MESSAGE.TITLE') }}
            </span>
            <ToggleSwitch v-model="kanbanBulkMessageEnabled" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.OTHER_FEATURES.KANBAN_BULK_MESSAGE.DESCRIPTION')
            }}
          </p>
        </div>
      </div>

      <!-- v6.4.25: Toggle Labels Location (header vs sidebar) -->
      <div
        class="flex flex-col flex-1 p-6 m-[1px] outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex h-12 w-12 mb-4">
            <div
              class="flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-amber-500 to-yellow-600 text-white"
            >
              <i class="i-lucide-tag text-2xl" />
            </div>
          </div>
          <span
            v-tooltip="
              labelsInSidebar
                ? $t('INTEGRATION_APPS.STATUS.ENABLED')
                : $t('INTEGRATION_APPS.STATUS.DISABLED')
            "
            class="text-white p-0.5 rounded-full w-5 h-5 flex items-center justify-center"
            :class="labelsInSidebar ? 'bg-n-teal-9' : 'bg-n-slate-8'"
          >
            <i class="i-ph-check-bold text-sm" />
          </span>
        </div>
        <div class="flex flex-col m-0 flex-1">
          <div
            class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
          >
            <span class="text-base font-semibold">
              {{ $t('INTEGRATION_SETTINGS.OTHER_FEATURES.LABELS_LOCATION.TITLE') }}
            </span>
            <ToggleSwitch v-model="labelsInSidebar" :disabled="isUpdating" />
          </div>
          <p class="text-n-slate-11 text-sm">
            {{
              $t('INTEGRATION_SETTINGS.OTHER_FEATURES.LABELS_LOCATION.DESCRIPTION')
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
